import React, { useState } from 'react';
import { Button, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { StrategyOption } from '../../../shared/hooks/useStreamingChat';
import { DocumentSupportModal } from './DocumentSupportModal';

const { Text } = Typography;

// 简洁的策略选择容器 - 减少内边距
const StrategySelectionContainer = styled.div`
  margin: 12px 0;
  padding: 12px;
`;

// 策略选项按钮 - 减少高度和间距
const StrategyButton = styled(Button)<{ strategyId: string; isSelected?: boolean }>`
  width: calc(50% - 6px);
  height: 36px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border: 1px solid #d1d5db;
  background: ${props => props.isSelected ? '#3b82f6' : 'white'};
  color: ${props => props.isSelected ? 'white' : '#374151'};
  
  &:hover {
    border-color: #3b82f6;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// 策略选项行容器 - 减少间距
const StrategyRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

interface StrategySelectionMessageProps {
  strategies: StrategyOption[];
  message: string;
  onStrategySelect: (strategyId: string) => void;
  selectedStrategy?: string;
  showDocumentSupport?: boolean;
}

export const StrategySelectionMessage: React.FC<StrategySelectionMessageProps> = ({
  strategies,
  message,
  onStrategySelect,
  selectedStrategy,
  showDocumentSupport = false
}) => {
  const { t } = useTranslation();
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // console.log('🔍 StrategySelectionMessage接收到的selectedStrategy:', {
  //   selectedStrategy,
  //   strategiesCount: strategies?.length,
  //   strategyIds: strategies?.map(s => s.id)
  // });

  // 简化逻辑：如果消息包含"分析完了"或"第三段階完了"，就显示文档支持
  const shouldShowDocumentSupport = message.includes('分析完了') || message.includes('第三段階完了');
  
  // 如果选择的策略是"直接交渉で合意を目指す"，则不显示文档支持
  const shouldHideDocumentSupport = selectedStrategy === 'amicable_resolution';

  // 根据选中的策略返回对应的文档信息
  const getDocumentInfoForStrategy = (strategyId?: string) => {
    const documentMap = {
      'amicable_resolution': {
        title: '📋 交渉・和解書類サポート',
        description: 'この戦略で必要な書類についても詳しくご案内できます：',
        items: [
          '• 和解協議書テンプレート',
          '• 通知書テンプレート',
          '• 交渉記録テンプレート'
        ]
      },
      'mediation_conciliation': {
        title: '📋 調停・あっせん手続きサポート',
        description: '日本法院の調停手続きに基づいて、必要な書類と手続きをご案内します：',
        sections: [
          {
            title: '📝 申立に必要な書類',
            items: [
              '• 調停申立書（正本・副本）',
              '• 印鑑（認め印）',
              '• 手数料（収入印紙）',
              '• 郵便切手',
              '• 証拠書類の写し'
            ]
          },
          {
            title: '🏛️ 利用可能な調停機関',
            items: [
              '• 簡易裁判所民事調停',
              '• 家庭裁判所調停',
              '• 法務省認証ADR機関',
              '• 労働局あっせん',
              '• 消費者生活センター'
            ]
          },
          {
            title: '📋 手続きの流れ',
            items: [
              '• 申立書の作成・提出',
              '• 調停期日の通知',
              '• 調停委員会での話し合い',
              '• 合意書の作成',
              '• 調停の成立・終了'
            ]
          }
        ]
      },
      'self_legal_proceedings': {
        title: '⚖️ 自己訴訟手続きサポート',
        description: '日本法院の民事訴訟手続きに基づいて、必要な書類と手続きをご案内します：',
        sections: [
          {
            title: '📝 訴訟に必要な書類',
            items: [
              '• 訴状（正本・副本）',
              '• 印鑑（認め印、スタンプ式は不可）',
              '• 手数料（収入印紙）',
              '• 証拠書類の写し（1部）',
              '• 委任状（代理人がいる場合）',
              '• 法人の場合は法人登記事項証明書'
            ]
          },
          {
            title: '🏛️ 利用可能な裁判所',
            items: [
              '• 簡易裁判所（140万円以下の金銭請求等）',
              '• 地方裁判所（一般的な民事訴訟事件）',
              '• 家庭裁判所（家事事件：離婚、相続等）',
              '• 労働審判（労働問題に特化）',
              '• 知的財産高等裁判所（知的財産事件）'
            ]
          },
          {
            title: '📋 手続きの流れ',
            items: [
              '• 訴状の作成・提出（窓口または郵送）',
              '• 訴状副本の送達（被告への通知）',
              '• 答弁書の提出（被告側）',
              '• 口頭弁論期日の設定',
              '• 準備書面の提出',
              '• 証拠調べの実施',
              '• 判決の言渡し'
            ]
          }
        ]
      },
      'lawyer_legal_proceedings': {
        title: '⚖️ 弁護士依頼サポート',
        description: '弁護士への依頼に必要な書類と手続きをご案内します。適切な弁護士選定と依頼手続きをサポートします：',
        sections: [
          {
            title: '🏛️ 弁護士事務所の選択ガイド',
            items: [
              '• 個人事務所：費用が比較的安い、親身な対応',
              '• 中小事務所：専門分野の多様性、チーム対応',
              '• 大規模事務所：高度な専門性、豊富な実績'
            ]
          },
          {
            title: '📋 弁護士選定の重要基準',
            items: [
              '• 専門分野（重要度：高）',
              '• 実績・経験（重要度：高）',
              '• 費用（重要度：中）',
              '• 対応時間（重要度：中）',
              '• 相性（重要度：高）'
            ]
          },
          {
            title: '💰 弁護士費用の体系',
            items: [
              '• 相談料：5,000円～20,000円/時間',
              '• 着手金：10万円～100万円',
              '• 成功報酬：解決金額の10%～30%',
              '• 月額報酬：5万円～50万円/月'
            ]
          },
          {
            title: '📝 弁護士依頼に必要な書類',
            items: [
              '• 委任状',
              '• 委任契約書',
              '• 事件関連書類',
              '• 証拠書類',
              '• 身分証明書',
              '• 印鑑（認め印）'
            ]
          }
        ]
      }
    };
    
    return documentMap[strategyId as keyof typeof documentMap] || {
      title: '📋 書類作成サポート',
      description: 'この戦略で必要な書類についても詳しくご案内できます：',
      items: [
        '• 訴状・答弁書のテンプレート',
        '• 調停申立書の書き方',
        '• 委任状の作成方法'
      ]
    };
  };

  const documentInfo = getDocumentInfoForStrategy(selectedStrategy);

  // 将策略分组，每行2个
  const strategyRows = [];
  for (let i = 0; i < strategies.length; i += 2) {
    strategyRows.push(strategies.slice(i, i + 2));
  }

  // 渲染文档支持内容
  const renderDocumentSupportContent = () => {
    // 如果是调解策略、自己诉讼策略或依赖律师策略，使用新的sections结构
    if ((selectedStrategy === 'mediation_conciliation' || selectedStrategy === 'self_legal_proceedings' || selectedStrategy === 'lawyer_legal_proceedings') && 'sections' in documentInfo) {
      return (
        <div>
          <div style={{ 
            fontSize: '14px', 
            color: '#666', 
            marginBottom: '8px' 
          }}>
            {documentInfo.title}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginBottom: '12px',
            lineHeight: '1.6'
          }}>
            {documentInfo.description}
            <br />
            {documentInfo.sections.map((section, sectionIndex) => (
              <React.Fragment key={sectionIndex}>
                <br />
                <strong>{section.title}</strong>
                <br />
                {section.items.map((item, itemIndex) => (
                  <React.Fragment key={itemIndex}>
                    {item}
                    <br />
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      );
    }
    
    // 其他策略使用原来的items结构
    return (
      <div>
        <div style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginBottom: '8px' 
        }}>
          {documentInfo.title}
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: '#666', 
          marginBottom: '12px',
          lineHeight: '1.6'
        }}>
          {documentInfo.description}
          <br />
          {'items' in documentInfo && documentInfo.items.map((item, index) => (
            <React.Fragment key={index}>
              {item}
              <br />
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  return (
    <StrategySelectionContainer>
      {/* 策略选择提示 - 减少下边距 */}
      <div style={{ 
        fontSize: '14px', 
        color: '#374151', 
        marginBottom: '12px',
        lineHeight: '1.4'
      }}>
        {message}
      </div>
      
      {/* 策略选项按钮 - 每行2个 */}
      {strategyRows.map((row, rowIndex) => (
        <StrategyRow key={rowIndex}>
          {row.map((strategy) => (
            <StrategyButton
              key={strategy.id}
              strategyId={strategy.id}
              isSelected={selectedStrategy === strategy.id}
              onClick={() => onStrategySelect(strategy.id)}
            >
              {strategy.name}
            </StrategyButton>
          ))}
          {/* 如果这一行只有1个选项，添加空白占位 */}
          {row.length === 1 && <div style={{ width: 'calc(50% - 6px)' }} />}
        </StrategyRow>
      ))}
      

      
      {/* 文档支持按钮 */}
      {shouldShowDocumentSupport && !shouldHideDocumentSupport && (
        <div style={{ 
          marginTop: '16px', 
          padding: '12px',
          background: '#f8f9fa',
          borderRadius: '8px',
          borderLeft: '4px solid #1890ff'
        }}>
          {renderDocumentSupportContent()}
          <Button 
            type="default"
            size="small"
            onClick={() => setShowDocumentModal(true)}
            style={{
              borderRadius: '6px',
              borderColor: '#1890ff',
              color: '#1890ff'
            }}
          >
            書類について詳しく見る
          </Button>
        </div>
      )}
      
      {/* 文档支持模态框 */}
      <DocumentSupportModal
        visible={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        selectedStrategy={selectedStrategy}
      />
    </StrategySelectionContainer>
  );
}; 